<template>
  <div style="height: 100%; width: 100%; display: flex; flex-direction: column; overflow: hidden;">
    <!-- Debug controls -->

    <iframe
      :key="iframeKey"
      id="chainlit-iframe"
      ref="chainlitIframe"
      :src="iframeSrc"
      width="100%"
      height="100%"
      style="flex: 1; border: none; z-index: 1;"
      frameborder="0" allow="clipboard-write"
      @load="onIframeLoad"
    />
  </div>
</template>

  <script setup>
  // Prevent iframe caching
  useHead({
    meta: [
      { 'http-equiv': 'Cache-Control', content: 'no-cache, no-store, must-revalidate' },
      { 'http-equiv': 'Pragma', content: 'no-cache' },
      { 'http-equiv': 'Expires', content: '0' }
    ]
  })

  // Use official @nuxtjs/supabase composables
  const session = useSupabaseSession()
  const user = useSupabaseUser()
  const supabase = useSupabaseClient()
  const colorMode = useColorMode()
  const chainlitIframe = ref(null)

  // Use user store for team/group context
  const userStore = useUserStore()

  // Use credits composable for proper UI updates
  const { consumeCredits, fetchCredits } = useCredits()

  // Store session_id received from Chainlit
  const sessionId = ref('current_session') // fallback

  // Store pending plant schedule data to send when iframe is ready
  const pendingPlantScheduleData = ref(null)

  // Reactive iframe source with cache busting and auth parameters
  const config = useRuntimeConfig()
  const baseUrl = config.public.node_env === 'development'
    ? 'http://localhost:8000/chat'
    : 'https://chat.superseeded.ai/chat'
  const iframeKey = ref(Date.now()) // Force re-render when needed
  const iframeSrc = computed(() => {
    // Add timestamp to bust cache and auth parameters for header auth
    const params = new URLSearchParams()
    params.set('t', iframeKey.value.toString())

    // Add access token for standalone Chainlit authentication
    if (session.value?.access_token) {
      params.set('token', session.value.access_token)
    }

    // Add authentication parameters for iframe header auth (browser-safe approach)
    if (user.value?.id) {
      params.set('user_id', user.value.id)
      params.set('user_email', user.value.email || '')
      // Add additional user context for Chainlit authentication
      params.set('user_name', user.value.user_metadata?.full_name || user.value.user_metadata?.name || '')
      // Add subscription context for feature access control
      const subscriptionStatus = user.value?.app_metadata?.subscription_status || user.value?.raw_app_meta_data?.subscription_status || 'free'
      params.set('subscription_status', subscriptionStatus)
      params.set('is_active_subscription', ['active', 'trialing'].includes(subscriptionStatus).toString())
    }

    return `${baseUrl}?${params.toString()}`
  })

  // Determine the correct target origin for postMessage
  const getIframeOrigin = () => {
    // When sending messages to the iframe, use the iframe's origin
    const config = useRuntimeConfig()
    const isDevelopment = config.public.node_env === 'development'
    return isDevelopment ? 'http://localhost:8000' : 'https://chat.superseeded.ai'
  }

  const getTargetOrigin = () => {
    if (typeof window !== 'undefined') {
      return window.location.origin // For receiving messages (current origin)
    }
    return '*' // Fallback
  }

  // Create secure message signature
  const createMessageSignature = async (payload, timestamp) => {
    const message = JSON.stringify({
      ...payload,
      timestamp: timestamp
    })

    // Use a combination of user ID and timestamp as salt
    const salt = `${session.value?.user?.id || 'anonymous'}_${Math.floor(timestamp / 60000)}` // 1-minute window

    // Simple hash-based signature (client-side)
    const encoder = new TextEncoder()
    const data = encoder.encode(message + salt)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  // Function to refresh iframe
  const refreshIframe = () => {
    iframeKey.value = Date.now()
  }

  let lastSentToken = null
  let lastSentColorMode = null
  let messageListener = null
  let authStateChangeUnsubscribe = null

  // Helper function to get iframe safely
  const getIframe = () => {
    return chainlitIframe.value || document.querySelector('iframe') || document.getElementById('chainlit-iframe')
  }

  const sendAuthToIframe = async (token) => {
    const iframe = getIframe()
    if (!iframe?.contentWindow || !token || token === lastSentToken) return

    // Send both the original auth and MCP-specific headers

    sendMessageToIframe({
      type: 'SUPABASE_AUTH',
      token
    })

    // Send MCP authentication headers for Chainlit with full user context
    // Note: In Supabase, user metadata can be in different places depending on how it's accessed
    const groups = user.value?.app_metadata?.groups || user.value?.raw_app_meta_data?.groups || {};

    // Distinguish between subscription status (active/trialing/canceled etc) and plan (Basic/Pro/Teams)
    const subscriptionStatus = user.value?.app_metadata?.subscription_status || user.value?.raw_app_meta_data?.subscription_status || 'free';

    // Get the actual plan name from various possible locations
    const planName = user.value?.app_metadata?.plan?.product?.name ||
                    user.value?.raw_app_meta_data?.plan?.product?.name ||
                    user.value?.app_metadata?.plan_name ||
                    user.value?.raw_app_meta_data?.plan_name ||
                    user.value?.app_metadata?.plan ||
                    user.value?.raw_app_meta_data?.plan ||
                    'Basic'; // Default to Basic (free tier)

    // Determine if subscription is actually active (trialing counts as active for feature access)
    const isActiveSubscription = ['active', 'trialing'].includes(subscriptionStatus);

    // For trialing users, we'll assume they have access to Pro features
    const effectivePlan = subscriptionStatus === 'trialing' && planName === 'Basic' ? 'Pro' : planName;

    // Create a clean, serializable version of user metadata
    const userMetadata = {
      user_id: user.value?.id || 'anonymous',
      email: user.value?.email || '',
      // Include name fields for proper greeting personalization
      full_name: user.value?.user_metadata?.full_name || user.value?.user_metadata?.name || '',
      name: user.value?.user_metadata?.name || user.value?.user_metadata?.full_name || '',
      first_name: user.value?.user_metadata?.first_name || '',
      last_name: user.value?.user_metadata?.last_name || '',
      groups: (() => {
        try {
          return JSON.parse(JSON.stringify(groups || {}))
        } catch (error) {
          console.warn('Failed to serialize groups:', error)
          return {}
        }
      })(), // Deep clone to remove any circular refs
      subscription_status: subscriptionStatus,
      plan_name: planName,
      effective_plan: effectivePlan,
      is_active_subscription: isActiveSubscription,
      // Safely serialize metadata objects including user_metadata for nested access
      user_metadata: (() => {
        try {
          return JSON.parse(JSON.stringify(user.value?.user_metadata || {}))
        } catch (error) {
          console.warn('Failed to serialize user_metadata:', error)
          return {}
        }
      })(),
      raw_app_meta_data: (() => {
        try {
          return JSON.parse(JSON.stringify(user.value?.raw_app_meta_data || {}))
        } catch (error) {
          console.warn('Failed to serialize raw_app_meta_data:', error)
          return {}
        }
      })(),
      app_metadata: (() => {
        try {
          return JSON.parse(JSON.stringify(user.value?.app_metadata || {}))
        } catch (error) {
          console.warn('Failed to serialize app_metadata:', error)
          return {}
        }
      })()
    }


    const authMessage = {
      type: 'MCP_AUTH_HEADERS',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Agent-ID': 'superseeded-app',
        'X-User-ID': user.value?.id || 'anonymous',
        'X-User-Email': user.value?.email || '',
        'X-User-Groups': JSON.stringify(groups),
        'X-User-Subscription-Status': subscriptionStatus,
        'X-User-Plan': effectivePlan,
        'X-User-Is-Active': isActiveSubscription.toString(),
        'User-Agent': 'SuperSeeded-WebApp/1.0'
      },
      userMetadata,
      // Add iframe authentication context for Chainlit header auth callback
      iframeAuth: {
        user_id: user.value?.id || 'anonymous',
        user_email: user.value?.email || '',
        user_name: user.value?.user_metadata?.full_name || user.value?.user_metadata?.name || '',
        subscription_status: subscriptionStatus,
        is_active_subscription: isActiveSubscription
      }
    }

    // Send auth via postMessage only (no server proxy)
    // Removed cookie-based auth - using postMessage approach instead

    // CRITICAL: Send to Nuxt server-side API endpoint first to proxy auth to Chainlit
    try {
      const response = await $fetch('/api/auth/set-chainlit-auth', {
        method: 'POST',
        body: authMessage,
        credentials: 'include'  // Include cookies in the request
      })
      console.log('✅ ChatIframe: Sent auth via Nuxt server proxy:', response)
      console.log('🔑 Token being sent:', session.value?.access_token ? `${session.value.access_token.substring(0, 20)}...` : 'no token')
      
      // Persist auth cookies client-side as a fallback (local development only)
      try {
        const isDev = config.public.node_env === 'development'
        const cookieOpts = isDev
          ? '; path=/; samesite=lax' // localhost friendly
          : '; path=/; samesite=None; Secure'
        
        // Only set cookies if userMetadata is available and cookies are not set already
        if (response && response.status === 'success' && userMetadata) {
          const uid = userMetadata.user_id || userMetadata.userId || userMetadata.id || userMetadata.sub || userMetadata.user_id
          const email = userMetadata.email || ''
          if (uid) {
            document.cookie = `chainlit_user_id=${encodeURIComponent(uid)}${cookieOpts}`
            document.cookie = `chainlit_user_email=${encodeURIComponent(email)}${cookieOpts}`
            console.log('🍪 ChatIframe: Set fallback client-side cookies for chainlit_user_id/email')
          } else {
            console.log('🍪 ChatIframe: No user id in userMetadata to set cookies')
          }
        }
      } catch (err) {
        console.warn('⚠️ ChatIframe: Could not set fallback cookies client-side:', err)
      }

      // Also send via postMessage for immediate use
      sendMessageToIframe(authMessage)
      console.log('✅ ChatIframe: Also sent auth via postMessage for immediate use')
    } catch (error) {
      console.warn('⚠️ ChatIframe: Failed to set auth via server proxy:', error)
      // Fallback to direct postMessage if server proxy fails
      sendMessageToIframe(authMessage)
      console.log('🔄 ChatIframe: Fell back to direct postMessage auth')
    }

    // Then send via postMessage for immediate use
    
    sendMessageToIframe(authMessage)
    console.log('✅ ChatIframe: Sent auth via postMessage')

    lastSentToken = token
  }

  const sendColorModeToIframe = (mode) => {
    const iframe = getIframe()
    if (!iframe?.contentWindow || !mode || mode === lastSentColorMode) return

    const message = {
      type: 'COLOR_MODE',
      mode
    }

    sendMessageToIframe(message)
    lastSentColorMode = mode
  }

  const onIframeLoad = () => {
    // Send color mode immediately and with follow-up
    if (colorMode.value) {
      sendColorModeToIframe(colorMode.value)
      setTimeout(() => {
        sendColorModeToIframe(colorMode.value)
      }, 100)
    }

    // Send authentication data via both postMessage and URL parameters for comprehensive iframe auth
    if (session.value?.access_token) {
      sendAuthToIframe(session.value.access_token)
      // Additional follow-up auth send for reliability
      setTimeout(() => {
        sendAuthToIframe(session.value.access_token)
      }, 200)
    }

    // Send any pending plant schedule data
    if (pendingPlantScheduleData.value) {
      console.log('[ChatIframe] Sending pending plant schedule data on iframe load')
      sendMessageToIframe(pendingPlantScheduleData.value)
      pendingPlantScheduleData.value = null
    }
  }

  // Watch for color mode changes
  watch(() => colorMode.value, (newMode) => {
    if (newMode) {
      setTimeout(() => {
        sendColorModeToIframe(newMode)
      }, 100)
    }
  })

  onMounted(() => {
    messageListener = (event) => {


      // Listen for ready message from Chainlit
      if (event.data?.type === 'CHAINLIT_READY') {
        console.log('[ChatIframe] Received CHAINLIT_READY, sending auth and pending data')
        if (colorMode.value) {
          sendColorModeToIframe(colorMode.value)
        }
        if (session.value?.access_token) {
          sendAuthToIframe(session.value.access_token)
        }
        // Send any pending plant schedule data
        if (pendingPlantScheduleData.value) {
          console.log('[ChatIframe] Sending pending plant schedule data on CHAINLIT_READY')
          sendMessageToIframe(pendingPlantScheduleData.value)
          pendingPlantScheduleData.value = null
        }
        return
      }

      // Handle iframe authentication ready signal (complements CHAINLIT_READY)
      if (event.data?.type === 'IFRAME_AUTH_READY') {
        console.log('🔐 ChatIframe: Received iframe auth ready signal, sending comprehensive auth data')
        if (session.value?.access_token) {
          sendAuthToIframe(session.value.access_token)
        }
        return
      }

      // Handle color mode requests from iframe
      if (event.data?.type === 'REQUEST_COLOR_MODE') {
        if (colorMode.value) {
          sendColorModeToIframe(colorMode.value)
        }
        return
      }

      // Handle requests for MCP auth headers
      if (event.data === 'REQUEST_MCP_AUTH_HEADERS' || event.data?.type === 'REQUEST_MCP_AUTH_HEADERS') {
        if (session.value?.access_token) {
          sendAuthToIframe(session.value.access_token)
        } else {
          const iframe = getIframe()
          iframe?.contentWindow?.postMessage({
            type: 'AUTH_FAILED'
          }, getIframeOrigin())
        }
        return
      }

      // Handle requests for iframe header authentication (browser-safe approach)
      if (event.data?.type === 'REQUEST_IFRAME_AUTH') {
        if (session.value?.access_token) {
          // Send iframe-specific auth data via postMessage (complements URL parameters)
          const iframeAuthMessage = {
            type: 'IFRAME_AUTH_DATA',
            user_id: user.value?.id || 'anonymous',
            user_email: user.value?.email || '',
            user_name: user.value?.user_metadata?.full_name || user.value?.user_metadata?.name || '',
            subscription_status: user.value?.app_metadata?.subscription_status || user.value?.raw_app_meta_data?.subscription_status || 'free',
            is_active_subscription: ['active', 'trialing'].includes(user.value?.app_metadata?.subscription_status || user.value?.raw_app_meta_data?.subscription_status || 'free'),
            token: session.value.access_token
          }
          sendMessageToIframe(iframeAuthMessage)
        } else {
          const iframe = getIframe()
          iframe?.contentWindow?.postMessage({
            type: 'AUTH_FAILED'
          }, getIframeOrigin())
        }
        return
      }

      // Handle auth token request from iframe
      if (event.data?.type === 'REQUEST_AUTH_TOKEN') {

        if (session.value?.access_token) {
          const iframe = getIframe()
          iframe?.contentWindow?.postMessage({
            type: 'AUTH_TOKEN',
            token: session.value.access_token
          }, getIframeOrigin())

        } else {

          const iframe = getIframe()
          iframe?.contentWindow?.postMessage({
            type: 'AUTH_FAILED'
          }, getIframeOrigin())
        }
        return
      }

      // Handle session info from Chainlit
      if (event.data?.type === 'SESSION_INFO') {
        sessionId.value = event.data.session_id

        return
      }

      // Validate origin
      const allowedOrigins = ['https://chat.superseeded.ai', 'http://localhost:8000', 'https://superseeded.ai', 'http://localhost:3000']

      if (!allowedOrigins.some(origin => event.origin === origin || event.origin.startsWith(origin))) {
        return
      }


      // Handle auth requests
      if (event.data?.type === 'REQUEST_SUPABASE_AUTH' || event.data?.type === 'REQUEST_AUTH') {
        if (session.value?.access_token) {
          sendAuthToIframe(session.value.access_token)
        } else {
          const iframe = getIframe()
          iframe?.contentWindow?.postMessage({
            type: 'AUTH_FAILED'
          }, baseUrl)
        }
      }

      // Handle iframe header authentication requests (complements URL parameter approach)
      if (event.data?.type === 'REQUEST_IFRAME_HEADERS') {
        if (session.value?.access_token) {
          // Send header-compatible auth data for Chainlit's header_auth_callback
          const headerAuthMessage = {
            type: 'IFRAME_HEADER_AUTH',
            headers: {
              'x-user-id': user.value?.id || 'anonymous',
              'x-user-email': user.value?.email || '',
              'x-user-name': user.value?.user_metadata?.full_name || user.value?.user_metadata?.name || '',
              'x-subscription-status': user.value?.app_metadata?.subscription_status || user.value?.raw_app_meta_data?.subscription_status || 'free',
              'x-is-active-subscription': ['active', 'trialing'].includes(user.value?.app_metadata?.subscription_status || user.value?.raw_app_meta_data?.subscription_status || 'free').toString()
            },
            userMetadata: {
              user_id: user.value?.id || 'anonymous',
              email: user.value?.email || '',
              full_name: user.value?.user_metadata?.full_name || user.value?.user_metadata?.name || '',
              subscription_status: user.value?.app_metadata?.subscription_status || user.value?.raw_app_meta_data?.subscription_status || 'free',
              is_active_subscription: ['active', 'trialing'].includes(user.value?.app_metadata?.subscription_status || user.value?.raw_app_meta_data?.subscription_status || 'free')
            }
          }
          sendMessageToIframe(headerAuthMessage)
        } else {
          const iframe = getIframe()
          iframe?.contentWindow?.postMessage({
            type: 'AUTH_FAILED'
          }, getIframeOrigin())
        }
        return
      }

      // Handle experts purchased - no longer forwarding to Chainlit injection endpoint
      if (event.data?.type === 'experts_purchased') {
        const { results, query, count, totalSpent } = event.data

        // Experts are now handled directly in the purchase flow
        // No need to forward to Chainlit injection endpoint

        // Optional: Refresh user credits display if composable available
        // if (typeof useCredits !== 'undefined') {
        //   useCredits().refresh()
        // }
      }

      // Handle expert purchase request from iframe
      if (event.data?.type === 'request_expert_purchase') {

        const { amount, expertCount, selectedResults, query, results } = event.data

        // Instead of consuming credits here, send message to Chainlit to call MCP purchase tool
        const iframe = getIframe()
        iframe?.contentWindow?.postMessage({
          type: 'call_purchase_tool',
          selectedResults: selectedResults,
          query: query || '',
          expertCount: expertCount,
          amount: amount
        }, getIframeOrigin())
      }

      // Handle credit balance update from iframe (after MCP purchase)
      if (event.data?.type === 'CREDIT_BALANCE_UPDATE' && event.data?.action === 'refresh_credits') {
        console.log('💳 Chat.vue: Received credit balance update request from iframe, refreshing credits...')
        fetchCredits()
        console.log('💳 Chat.vue: Credit balance refresh initiated after advice purchase')
      }
    }

    window.addEventListener('message', messageListener)

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (newSession?.access_token) {
        sendAuthToIframe(newSession.access_token)
      } else if (event === 'SIGNED_OUT' || !session.value?.access_token) {
        lastSentToken = null
        const iframe = getIframe()
        iframe?.contentWindow?.postMessage({
          type: 'AUTH_FAILED'
        }, baseUrl)
      }
    })

    authStateChangeUnsubscribe = () => subscription.unsubscribe()
  })

  onUnmounted(() => {
    if (messageListener) {
      window.removeEventListener('message', messageListener)
    }
    if (authStateChangeUnsubscribe) {
      authStateChangeUnsubscribe()
    }
    lastSentToken = null
    lastSentColorMode = null
    pendingPlantScheduleData.value = null
  })


  // Send message to iframe with serialization safety
  const sendMessageToIframe = (message) => {
    const iframe = getIframe()
    if (iframe?.contentWindow) {
      try {
        const serializedMessage = JSON.parse(JSON.stringify(message))
        iframe.contentWindow.postMessage(serializedMessage, getIframeOrigin())
        iframe.contentWindow.postMessage(serializedMessage, '*') // Fallback
      } catch (error) {
        const safeMessage = {
          type: message.type || 'UNKNOWN',
          error: 'Message serialization failed',
          original_error: error.message
        }
        iframe.contentWindow.postMessage(safeMessage, getIframeOrigin())
        iframe.contentWindow.postMessage(safeMessage, '*')
      }
    }
  }



  /**
   * Send plant schedule grid data to chat iframe
   * Transforms grid data using the panel data transformer and sends it to the chat
   * @param {any} gridData - The plant grid data to send
   * @param {string} sourceToolId - The source tool ID (default: 'plantgrid')
   */
  const sendGridDataToChat = async (gridData, sourceToolId = 'plantgrid') => {
    try {
      console.log('[ChatIframe] Sending grid data to chat:', { sourceToolId, gridData })

      // Get the panel data transformer
      const { transformData } = usePanelDataTransformers()

      // Transform the grid data for chat display
      const transformedData = await transformData(gridData, sourceToolId, 'chat')

      console.log('[ChatIframe] Transformed grid data:', transformedData)

      // Prepare the message
      const messageData = {
        type: 'PLANT_SCHEDULE_DATA',
        data: transformedData,
        timestamp: Date.now()
      }

      // Check if iframe is ready, if not, store for later
      const iframe = getIframe()
      if (iframe?.contentWindow) {
        // Send immediately if iframe is ready
        sendMessageToIframe(messageData)
        console.log('[ChatIframe] Plant schedule data sent to chat successfully')
      } else {
        // Store for sending when iframe loads
        pendingPlantScheduleData.value = messageData
        console.log('[ChatIframe] Plant schedule data stored for sending when iframe loads')
      }
    } catch (error) {
      console.error('[ChatIframe] Error sending grid data to chat:', error)
    }
  }

  // Expose the method so it can be called from parent components
  defineExpose({
    sendGridDataToChat
  })
  </script>